import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import useAuthenticatedAxios from "../../../auth/AuthenticatedAxios";
import ImavenLoadingScreen from '../../../Components/Helpers/loading-screen';
import DOMPurify from 'dompurify';
import { useAIAssistant } from '../../../Components/AIAssistant';
import ToastrService from '../../../services/toaster.service';

// ============================================
// SVG ICONS (Simple, monochrome style)
// ============================================

const Icons = {
  list: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
  flow: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/></svg>,
  trash: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  save: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  check: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  refresh: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
  loader: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>,
};

// Spinning loader component for loading states
const SpinnerIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const ModuleContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  gap: 0;
`;

const Sidebar = styled.div`
  width: 350px;
  min-width: 350px;
  height: 100%;
  background: rgba(15, 20, 35, 0.95);
  border-right: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
`;

const SidebarTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #a5b4fc;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const SearchInput = styled.input`
  width: 100%;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 10px 12px;
  color: #fff;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const RuleSetList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;

  /* Hide scrollbar but keep scroll functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const RuleSetCard = styled.div`
  background: ${props => props.selected 
    ? 'rgba(99, 102, 241, 0.2)' 
    : 'rgba(25, 30, 45, 0.6)'};
  border: 1px solid ${props => props.selected 
    ? 'rgba(99, 102, 241, 0.5)' 
    : 'rgba(99, 102, 241, 0.15)'};
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(99, 102, 241, 0.4);
    background: rgba(99, 102, 241, 0.1);
  }
`;

const RuleSetCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const RuleSetCardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
`;

const RuleSetCardStatus = styled.span`
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 10px;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => props.status === 'active' && `
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
  `}
  
  ${props => props.status === 'draft' && `
    background: rgba(251, 191, 36, 0.15);
    color: #fbbf24;
  `}
  
  ${props => props.status === 'archived' && `
    background: rgba(156, 163, 175, 0.15);
    color: #9ca3af;
  `}
`;

const RuleSetCardDescription = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 8px;
`;

const RuleSetCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
`;

// Workflow Progress Banner
const WorkflowBanner = styled.div`
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  margin: 0 16px 16px 16px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const WorkflowSteps = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const WorkflowStep = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  transition: all 0.2s ease;
  
  ${props => props.$completed && `
    background: rgba(16, 185, 129, 0.2);
    color: #10b981;
  `}
  
  ${props => props.$current && !props.$completed && `
    background: rgba(251, 191, 36, 0.2);
    color: #fbbf24;
    animation: pulse 2s ease-in-out infinite;
  `}
  
  ${props => !props.$completed && !props.$current && `
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.3);
  `}
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const WorkflowArrow = styled.span`
  color: rgba(255, 255, 255, 0.2);
  font-size: 16px;
`;

const WorkflowHint = styled.div`
  font-size: 11px;
  color: rgba(251, 191, 36, 0.9);
  background: rgba(251, 191, 36, 0.1);
  padding: 6px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: rgba(10, 15, 25, 0.8);
  overflow: hidden;
`;

const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: rgba(15, 20, 35, 0.9);
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
`;

const ContentTitle = styled.input`
  background: transparent;
  border: none;
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  width: 300px;

  &:focus {
    outline: none;
    border-bottom: 2px solid rgba(99, 102, 241, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.primary ? `
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border: none;
    color: #fff;
    
    &:hover {
      box-shadow: 0 4px 15px rgba(99, 102, 241, 0.4);
    }
  ` : props.danger ? `
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #ef4444;
    
    &:hover {
      background: rgba(239, 68, 68, 0.2);
    }
  ` : props.success ? `
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.3);
    color: #10b981;
    
    &:hover {
      background: rgba(16, 185, 129, 0.2);
    }
  ` : `
    background: rgba(99, 102, 241, 0.1);
    border: 1px solid rgba(99, 102, 241, 0.3);
    color: #a5b4fc;
    
    &:hover {
      background: rgba(99, 102, 241, 0.2);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ContentActions = styled.div`
  display: flex;
  gap: 10px;
`;

const ContentBody = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
`;

const RulesPanel = styled.div`
  width: 50%;
  border-right: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const DirectivePanel = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: 14px 20px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(15, 20, 35, 0.5);
`;

const PanelTitle = styled.h4`
  font-size: 13px;
  font-weight: 600;
  color: #a5b4fc;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 4px;
  }
`;

const RuleCard = styled.div`
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-left: 4px solid ${props => props.isActive ? '#10b981' : '#6b7280'};
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 12px;
`;

const RuleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const RuleName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
`;

const RulePriority = styled.span`
  font-size: 10px;
  padding: 2px 8px;
  background: rgba(99, 102, 241, 0.2);
  border-radius: 10px;
  color: #a5b4fc;
`;

const RuleDescription = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 10px;
`;

const RuleConditions = styled.div`
  background: rgba(15, 20, 30, 0.5);
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 8px;
`;

const RuleConditionsLabel = styled.div`
  font-size: 10px;
  color: rgba(165, 180, 252, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const Condition = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  padding: 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  span.field {
    color: #3b82f6;
  }

  span.operator {
    color: #f59e0b;
  }

  span.value {
    color: #10b981;
  }
`;

const RuleActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const RuleActionBadge = styled.span`
  font-size: 11px;
  padding: 4px 10px;
  background: rgba(139, 92, 246, 0.2);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 12px;
  color: #a78bfa;
`;

const DirectiveDisplay = styled.div`
  flex: 1;
  background: rgba(20, 25, 40, 0.5);
  border-radius: 8px;
  padding: 16px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
  line-height: 1.7;
  white-space: pre-wrap;
  overflow-y: auto;

  h1, h2, h3, h4 {
    color: #a5b4fc;
    margin: 16px 0 10px 0;
  }

  ul, ol {
    padding-left: 20px;
    margin: 10px 0;
  }

  li {
    margin-bottom: 6px;
  }
`;

const EmptyDirective = styled.div`
  text-align: center;
  padding: 40px;
  color: rgba(255, 255, 255, 0.3);
  font-style: italic;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.4);
  padding: 40px;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.5;
`;

const EmptyStateText = styled.div`
  font-size: 18px;
  margin-bottom: 10px;
`;

const EmptyStateSubtext = styled.div`
  font-size: 14px;
  opacity: 0.7;
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: rgba(20, 25, 40, 0.98);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  padding: 24px;
  width: 500px;
  max-width: 90%;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 20px 0;
`;

const ModalLabel = styled.label`
  display: block;
  font-size: 12px;
  color: rgba(165, 180, 252, 0.7);
  margin-bottom: 6px;
`;

const ModalSelect = styled.select`
  width: 100%;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 10px 12px;
  color: #fff;
  font-size: 14px;
  margin-bottom: 16px;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }

  option {
    background: #1a1f2e;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
`;

// Confirmation Modal animations and styles
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { 
    opacity: 0;
    transform: translate(-50%, -40%);
  }
  to { 
    opacity: 1;
    transform: translate(-50%, -50%);
  }
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: ${fadeIn} 0.2s ease-out;
`;

const ConfirmModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(135deg, rgba(25, 30, 45, 0.98) 0%, rgba(30, 35, 50, 0.98) 100%);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 16px;
  padding: 28px;
  width: 420px;
  max-width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(239, 68, 68, 0.1);
  animation: ${slideUp} 0.25s ease-out;
`;

const ConfirmIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.15);
  border: 2px solid rgba(239, 68, 68, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  
  svg {
    width: 28px;
    height: 28px;
    color: #ef4444;
  }
`;

const ConfirmTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 12px 0;
  text-align: center;
`;

const ConfirmMessage = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 24px 0;
  text-align: center;
  line-height: 1.6;
`;

const ConfirmItemName = styled.span`
  color: #a5b4fc;
  font-weight: 600;
`;

const ConfirmButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const ConfirmButton = styled.button`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;

  ${props => props.$danger ? `
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    border: none;
    color: #fff;
    
    &:hover {
      box-shadow: 0 4px 20px rgba(239, 68, 68, 0.4);
      transform: translateY(-1px);
    }
  ` : `
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.8);
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.3);
    }
  `}
`;

// ============================================
// MAIN COMPONENT
// ============================================

const RuleSetsModule = () => {
  const authenticatedAxios = useAuthenticatedAxios();
  
  // AI Assistant context
  const { updateContext } = useAIAssistant();

  // State
  const [ruleSets, setRuleSets] = useState([]);
  const [selectedRuleSet, setSelectedRuleSet] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [isGeneratingDirective, setIsGeneratingDirective] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [targetAgent, setTargetAgent] = useState('observer');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Update AI Assistant context when state changes
  useEffect(() => {
    updateContext({
      currentModule: 'rule-sets',
      moduleName: 'Rule Sets Library',
      moduleDescription: 'Manage generated rules',
      selectedElements: {
        ruleSet: selectedRuleSet ? {
          id: selectedRuleSet._id,
          name: selectedRuleSet.name,
          ruleCount: selectedRuleSet.rules?.length || 0,
          hasDirective: !!selectedRuleSet.generatedDirective,
          status: selectedRuleSet.status
        } : null,
      },
      availableActions: [
        ...(selectedRuleSet ? [
          'saveRuleSet',
          'deleteRuleSet',
          'generateDirective',
          'convertToFlow',
          ...(selectedRuleSet.generatedDirective ? ['applyDirective'] : [])
        ] : []),
      ],
      uiState: {
        ruleSetCount: ruleSets.length,
        isEditing: !!selectedRuleSet,
        showApplyModal,
        isSaving,
        isGeneratingDirective,
        targetAgent
      }
    });
  }, [selectedRuleSet, ruleSets.length, showApplyModal, isSaving, 
      isGeneratingDirective, targetAgent, updateContext]);

  // Load rule sets on mount
  useEffect(() => {
    loadRuleSets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRuleSets = async () => {
    try {
      setIsLoading(true);
      const response = await authenticatedAxios.get('/rule-sets');
      setRuleSets(response.data.ruleSets || []);
    } catch (error) {
      console.error('Error loading rule sets:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load selected rule set
  const handleSelectRuleSet = async (ruleSet) => {
    try {
      const response = await authenticatedAxios.get(`/rule-sets/${ruleSet._id}`);
      setSelectedRuleSet(response.data.ruleSet);
    } catch (error) {
      console.error('Error loading rule set:', error);
    }
  };

  // Save rule set
  const handleSave = async () => {
    if (!selectedRuleSet) return;
    setIsSaving(true);

    try {
      await authenticatedAxios.put(`/rule-sets/${selectedRuleSet._id}`, {
        name: selectedRuleSet.name,
        description: selectedRuleSet.description,
        rules: selectedRuleSet.rules
      });
      await loadRuleSets();
    } catch (error) {
      console.error('Error saving rule set:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Delete rule set - show confirmation modal
  const handleDeleteClick = () => {
    if (!selectedRuleSet) return;
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedRuleSet) return;
    setShowDeleteConfirm(false);
    
    try {
      await authenticatedAxios.delete(`/rule-sets/${selectedRuleSet._id}`);
      ToastrService.Success(`Rule set "${selectedRuleSet.name}" deleted successfully`);
      setSelectedRuleSet(null);
      await loadRuleSets();
    } catch (error) {
      console.error('Error deleting rule set:', error);
      ToastrService.Error('Failed to delete rule set. Please try again.');
    }
  };

  // Generate directive from rules
  const handleGenerateDirective = async () => {
    if (!selectedRuleSet) return;
    setIsGeneratingDirective(true);

    try {
      const response = await authenticatedAxios.post(`/scenario-flows/ruleset/${selectedRuleSet._id}/generate-directive`);
      setSelectedRuleSet(prev => ({
        ...prev,
        generatedDirective: response.data.directive,
        directiveGeneratedAt: new Date()
      }));
      ToastrService.Success('Directive generated successfully!');
    } catch (error) {
      console.error('Error generating directive:', error);
      ToastrService.Error('Failed to generate directive. Please try again.');
    } finally {
      setIsGeneratingDirective(false);
    }
  };

  // Apply directive to agent
  const handleApplyDirective = async () => {
    if (!selectedRuleSet?.generatedDirective) return;

    try {
      const response = await authenticatedAxios.post(`/rule-sets/${selectedRuleSet._id}/apply-directive`, {
        targetAgent
      });
      
      // Update the selected rule set with the returned data (includes appliedToAgent)
      const updatedRuleSet = response.data.ruleSet;
      if (updatedRuleSet) {
        setSelectedRuleSet(updatedRuleSet);
        // Also update the rule set in the list
        setRuleSets(prev => prev.map(rs => 
          rs._id === updatedRuleSet._id ? updatedRuleSet : rs
        ));
      }
      
      setShowApplyModal(false);
      ToastrService.Success(`Directive applied to ${targetAgent} agent successfully!`);
    } catch (error) {
      console.error('Error applying directive:', error);
      ToastrService.Error('Failed to apply directive. Please try again.');
    }
  };

  // Convert to flow
  const handleConvertToFlow = async () => {
    if (!selectedRuleSet || isConverting) return;

    setIsConverting(true);
    
    try {
      const response = await authenticatedAxios.post(`/rule-sets/${selectedRuleSet._id}/convert/to-flow`);
      ToastrService.Success(`Scenario flow "${response.data.flow.name}" created! Find it in the Scenario Flows module.`);
    } catch (error) {
      console.error('Error converting to flow:', error);
      ToastrService.Error('Failed to convert to flow. Please try again.');
    } finally {
      setIsConverting(false);
    }
  };

  // Filter rule sets
  const filteredRuleSets = ruleSets.filter(rs => 
    rs.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    rs.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return <ImavenLoadingScreen />;
  }

  return (
    <ModuleContainer>
      {/* Sidebar */}
      <Sidebar>
        <SidebarHeader>
          <SidebarTitle>Rule Sets Library</SidebarTitle>
          <SearchInput
            placeholder="Search rule sets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SidebarHeader>

        <RuleSetList>
          {filteredRuleSets.length === 0 && (
            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', padding: '20px' }}>
              No rule sets found. Generate one from a Scenario Flow.
            </div>
          )}
          {filteredRuleSets.map((ruleSet) => (
            <RuleSetCard 
              key={ruleSet._id} 
              selected={selectedRuleSet?._id === ruleSet._id}
              onClick={() => handleSelectRuleSet(ruleSet)}
            >
              <RuleSetCardHeader>
                <RuleSetCardTitle>{ruleSet.name}</RuleSetCardTitle>
                <RuleSetCardStatus status={ruleSet.status}>{ruleSet.status}</RuleSetCardStatus>
              </RuleSetCardHeader>
              {ruleSet.description && (
                <RuleSetCardDescription>{ruleSet.description}</RuleSetCardDescription>
              )}
              <RuleSetCardMeta>
                <span>{Icons.list} {ruleSet.rules?.length || 0} rules</span>
                {ruleSet.generatedDirective && <span>{Icons.check} Directive</span>}
              </RuleSetCardMeta>
            </RuleSetCard>
          ))}
        </RuleSetList>
      </Sidebar>

      {/* Main Content */}
      <MainContent>
        {selectedRuleSet ? (
          <>
            <ContentHeader>
              <ContentTitle
                value={selectedRuleSet.name}
                onChange={(e) => setSelectedRuleSet(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Rule set name..."
              />
              <ContentActions>
                <ActionButton onClick={handleConvertToFlow} disabled={isConverting}>
                  {isConverting ? <SpinnerIcon>{Icons.loader}</SpinnerIcon> : Icons.flow} {isConverting ? 'Converting...' : 'To Flow'}
                </ActionButton>
                <ActionButton danger onClick={handleDeleteClick} disabled={isConverting}>
                  {Icons.trash} Delete
                </ActionButton>
                <ActionButton primary onClick={handleSave} disabled={isSaving || isConverting}>
                  {isSaving ? <><SpinnerIcon>{Icons.loader}</SpinnerIcon> Saving...</> : <>{Icons.save} Save</>}
                </ActionButton>
              </ContentActions>
            </ContentHeader>

            {/* Workflow Progress Banner */}
            <WorkflowBanner>
              <WorkflowSteps>
                <WorkflowStep $completed={true}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3"/>
                  </svg>
                  1. Create Flow
                </WorkflowStep>
                <WorkflowArrow>→</WorkflowArrow>
                <WorkflowStep $completed={true}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3"/>
                  </svg>
                  2. Convert to Rules
                </WorkflowStep>
                <WorkflowArrow>→</WorkflowArrow>
                <WorkflowStep $completed={!!selectedRuleSet.generatedDirective} $current={!selectedRuleSet.generatedDirective}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {selectedRuleSet.generatedDirective ? <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3"/> : <circle cx="12" cy="12" r="10"/>}
                  </svg>
                  3. Generate Directive
                </WorkflowStep>
                <WorkflowArrow>→</WorkflowArrow>
                <WorkflowStep $completed={selectedRuleSet.appliedToAgent} $current={selectedRuleSet.generatedDirective && !selectedRuleSet.appliedToAgent}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {selectedRuleSet.appliedToAgent ? <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3"/> : <circle cx="12" cy="12" r="10"/>}
                  </svg>
                  4. Apply to Agent
                </WorkflowStep>
              </WorkflowSteps>
              {!selectedRuleSet.generatedDirective && (
                <WorkflowHint>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  Click "Generate" to create directive →
                </WorkflowHint>
              )}
              {selectedRuleSet.generatedDirective && !selectedRuleSet.appliedToAgent && selectedRuleSet.status === 'active' && (
                <WorkflowHint style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3"/>
                  </svg>
                  Widget: Active — Perfect Brain loads this automatically
                </WorkflowHint>
              )}
              {selectedRuleSet.generatedDirective && !selectedRuleSet.appliedToAgent && selectedRuleSet.status !== 'active' && (
                <WorkflowHint>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  Set status to "Active" for Widget, or click "Apply" for Observer/Summarizer
                </WorkflowHint>
              )}
              {selectedRuleSet.appliedToAgent && (
                <WorkflowHint style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3"/>
                  </svg>
                  Applied to {selectedRuleSet.appliedToAgent} agent
                </WorkflowHint>
              )}
            </WorkflowBanner>

            <ContentBody>
              {/* Rules Panel */}
              <RulesPanel>
                <PanelHeader>
                  <PanelTitle>{Icons.list} Rules ({selectedRuleSet.rules?.length || 0})</PanelTitle>
                </PanelHeader>
                <PanelContent>
                  {selectedRuleSet.rules?.length === 0 && (
                    <EmptyDirective>No rules defined yet.</EmptyDirective>
                  )}
                  {selectedRuleSet.rules?.map((rule, idx) => (
                    <RuleCard key={rule.id || idx} isActive={rule.isActive}>
                      <RuleHeader>
                        <RuleName>{rule.name}</RuleName>
                        <RulePriority>Priority: {rule.priority}</RulePriority>
                      </RuleHeader>
                      {rule.description && (
                        <RuleDescription>{rule.description}</RuleDescription>
                      )}
                      {rule.conditions?.length > 0 && (
                        <RuleConditions>
                          <RuleConditionsLabel>
                            Conditions ({rule.conditionLogic || 'AND'})
                          </RuleConditionsLabel>
                          {rule.conditions.map((cond, cIdx) => (
                            <Condition key={cIdx}>
                              <span className="field">{cond.field}</span>
                              <span className="operator">{cond.operator}</span>
                              <span className="value">"{String(cond.value)}"</span>
                            </Condition>
                          ))}
                        </RuleConditions>
                      )}
                      {rule.actions?.length > 0 && (
                        <RuleActions>
                          {rule.actions.map((action, aIdx) => (
                            <RuleActionBadge key={aIdx}>
                              {action.type.replace(/_/g, ' ')}
                            </RuleActionBadge>
                          ))}
                        </RuleActions>
                      )}
                    </RuleCard>
                  ))}
                </PanelContent>
              </RulesPanel>

              {/* Directive Panel */}
              <DirectivePanel>
                <PanelHeader>
                  <PanelTitle>{Icons.list} Generated Directive</PanelTitle>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {selectedRuleSet.generatedDirective && (
                      <ActionButton success onClick={() => setShowApplyModal(true)}>
                        {Icons.check} Apply
                      </ActionButton>
                    )}
                    <ActionButton 
                      onClick={handleGenerateDirective}
                      disabled={isGeneratingDirective}
                    >
                      {isGeneratingDirective ? <><SpinnerIcon>{Icons.loader}</SpinnerIcon> Generating...</> : <>{Icons.refresh} Generate</>}
                    </ActionButton>
                  </div>
                </PanelHeader>
                <PanelContent>
                  {selectedRuleSet.generatedDirective ? (
                    <DirectiveDisplay>
                      <div dangerouslySetInnerHTML={{ 
                        __html: DOMPurify.sanitize(selectedRuleSet.generatedDirective) 
                      }} />
                    </DirectiveDisplay>
                  ) : (
                    <EmptyDirective>
                      No directive generated yet. Click "Generate" to create one from the rules.
                    </EmptyDirective>
                  )}
                </PanelContent>
              </DirectivePanel>
            </ContentBody>
          </>
        ) : (
          <EmptyState>
            <EmptyStateIcon>{Icons.list}</EmptyStateIcon>
            <EmptyStateText>Select a rule set to view details</EmptyStateText>
            <EmptyStateSubtext>
              Rule sets are generated from Scenario Flows and can be converted to directives
            </EmptyStateSubtext>
          </EmptyState>
        )}
      </MainContent>

      {/* Apply Directive Modal */}
      {showApplyModal && (
        <ModalOverlay onClick={() => setShowApplyModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalTitle>Apply Directive to Agent</ModalTitle>
            
            <ModalLabel>Select Target Agent</ModalLabel>
            <ModalSelect
              value={targetAgent}
              onChange={(e) => setTargetAgent(e.target.value)}
            >
              <option value="observer">Observer Agent</option>
              <option value="summarizer">Summarizer Agent</option>
            </ModalSelect>

            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '16px' }}>
              This will append the generated directive to the selected agent's configuration.
              Widget rules are loaded automatically by Perfect Brain at runtime — no manual apply needed.
            </p>

            <ModalButtons>
              <ActionButton onClick={() => setShowApplyModal(false)}>
                Cancel
              </ActionButton>
              <ActionButton primary onClick={handleApplyDirective}>
                {Icons.check} Apply Directive
              </ActionButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <ConfirmOverlay onClick={() => setShowDeleteConfirm(false)}>
          <ConfirmModal onClick={e => e.stopPropagation()}>
            <ConfirmIcon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                <line x1="10" y1="11" x2="10" y2="17"/>
                <line x1="14" y1="11" x2="14" y2="17"/>
              </svg>
            </ConfirmIcon>
            <ConfirmTitle>Delete Rule Set?</ConfirmTitle>
            <ConfirmMessage>
              Are you sure you want to delete <ConfirmItemName>"{selectedRuleSet?.name}"</ConfirmItemName>? 
              This action cannot be undone.
            </ConfirmMessage>
            <ConfirmButtons>
              <ConfirmButton onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </ConfirmButton>
              <ConfirmButton $danger onClick={handleConfirmDelete}>
                Delete
              </ConfirmButton>
            </ConfirmButtons>
          </ConfirmModal>
        </ConfirmOverlay>
      )}
    </ModuleContainer>
  );
};

export default RuleSetsModule;
